import { ref } from 'vue'
import { aiUsageApi } from '@/api/aiUsage'
import { createRequestGuard, withAsyncState } from '@/lib/asyncState'
import type { AiUsageLog, AiUsageLogFilters, AiUsageLogSummary, PaginationMeta } from '@/types/models'

// Singleton, following useReviewRuns.ts's shape: shared state so the AI usage
// view retains its list/pagination across navigation within a session.
const aiUsageLogs = ref<AiUsageLog[]>([])
const currentLog = ref<AiUsageLog | null>(null)
// Every CLI call in the same run as currentLog (including currentLog itself),
// in call order — what the detail page's waterfall renders. Empty until
// fetchAiUsageLog() resolves, same as currentLog.
const currentRunCalls = ref<AiUsageLog[]>([])
const meta = ref<PaginationMeta | null>(null)
const summary = ref<AiUsageLogSummary | null>(null)
const availableModels = ref<string[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Guards against a stale filter/page request's response landing after a
// newer one's and clobbering it.
const requestGuard = createRequestGuard()

export function useAiUsageLogs() {
  async function fetchAiUsageLogs(filters: AiUsageLogFilters = {}): Promise<void> {
    const token = requestGuard.start()
    await withAsyncState(
      error,
      'Failed to load AI usage.',
      async () => {
        const page = await aiUsageApi.listAiUsageLogs(filters)
        if (!requestGuard.isCurrent(token)) return
        aiUsageLogs.value = page.data
        meta.value = page.meta
        summary.value = page.summary
        availableModels.value = page.available_models
      },
      loading,
      undefined,
      () => !requestGuard.isCurrent(token),
    )
  }

  async function fetchAiUsageLog(id: string): Promise<void> {
    // Clear the previous log so a stale entry never flashes while loading.
    currentLog.value = null
    currentRunCalls.value = []
    await withAsyncState(
      error,
      'Failed to load AI usage log.',
      async () => {
        const result = await aiUsageApi.getAiUsageLog(id)
        currentLog.value = result.data
        currentRunCalls.value = result.run_calls
      },
      loading,
    )
  }

  // Called on logout so the next session's AI usage view doesn't briefly show
  // the previous user's data before its own fetch resolves, and so a late
  // response from a request the previous session started can't land and
  // repopulate it.
  function reset(): void {
    requestGuard.start()
    aiUsageLogs.value = []
    currentLog.value = null
    currentRunCalls.value = []
    meta.value = null
    summary.value = null
    availableModels.value = []
    loading.value = false
    error.value = null
  }

  return {
    aiUsageLogs,
    currentLog,
    currentRunCalls,
    meta,
    summary,
    availableModels,
    loading,
    error,
    fetchAiUsageLogs,
    fetchAiUsageLog,
    reset,
  }
}
